'use client';

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';

import { createLogger } from '@core/be/platform/logging/Logger';

export type CanvasPoint = [number, number];

export interface UseMindmapLassoOptions {
  canvasRef: RefObject<HTMLDivElement | null>;
  mode: string;
  isEditing: boolean;
  hasHoveredNode: boolean;
}

export interface UseMindmapLassoResult {
  isLassoActive: boolean;
  lassoPath: CanvasPoint[];
  paneOrigin: { left: number; top: number } | null;
  allowLasso: boolean;
  consumePaneClickSuppression: () => boolean;
}

const DRAG_THRESHOLD = 6; // px

export function useMindmapLasso({ canvasRef, mode, isEditing, hasHoveredNode }: UseMindmapLassoOptions): UseMindmapLassoResult {
  const [isLassoActive, setIsLassoActive] = useState(false);
  const [lassoPath, setLassoPath] = useState<CanvasPoint[]>([]);
  const [paneOrigin, setPaneOrigin] = useState<{ left: number; top: number } | null>(null);

  const paneRectRef = useRef<DOMRect | null>(null);
  const lassoPendingRef = useRef<null | { rect: DOMRect; start: CanvasPoint }>(null);
  const suppressNextPaneClickRef = useRef(false);
  const isLassoActiveRef = useRef(isLassoActive);
  const pendingPointRef = useRef<CanvasPoint | null>(null);
  const pendingOriginRef = useRef<{ left: number; top: number } | null>(null);
  const rafRef = useRef<number | null>(null);

  const loggerRef = useRef(createLogger('mindmap:lasso-controller'));
  const logger = loggerRef.current;

  useEffect(() => {
    isLassoActiveRef.current = isLassoActive;
  }, [isLassoActive]);

  const allowLasso = useMemo(() => {
    if (mode !== 'select') return false;
    if (isLassoActive) return true;
    if (isEditing) return false;
    return !hasHoveredNode;
  }, [hasHoveredNode, isEditing, isLassoActive, mode]);

  const finishLasso = useCallback(() => {
    if (!isLassoActiveRef.current) return;
    paneRectRef.current = null;
    lassoPendingRef.current = null;
    pendingPointRef.current = null;
    pendingOriginRef.current = null;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setIsLassoActive(false);
    setLassoPath([]);
    setPaneOrigin(null);
    suppressNextPaneClickRef.current = true;
    logger.debug('lasso end');
  }, [logger]);

  useEffect(() => {
    const root = canvasRef.current;
    const pane = root?.querySelector('.react-flow__pane') as HTMLElement | null;
    const renderer = root?.querySelector('.react-flow__renderer') as HTMLElement | null;
    if (!pane || !renderer) return;

    const flushPending = () => {
      const point = pendingPointRef.current;
      const origin = pendingOriginRef.current;
      if (!point && !origin) {
        rafRef.current = null;
        return;
      }
      if (origin) {
        setPaneOrigin(origin);
        pendingOriginRef.current = null;
      }
      if (point) {
        setLassoPath((prev) => {
          const last = prev[prev.length - 1];
          if (last && last[0] === point[0] && last[1] === point[1]) return prev;
          return [...prev, point];
        });
        pendingPointRef.current = null;
      }
      rafRef.current = null;
    };

    const scheduleFlush = () => {
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(flushPending);
    };

    const isInteractive = (el: Element | null) =>
      !!el?.closest(
        '.react-flow__node, .react-flow__edge, .react-flow__controls, .react-flow__panel, .react-flow__minimap'
      );

    const shouldStartLasso = () => mode === 'select' && !isEditing && !hasHoveredNode && !isLassoActiveRef.current;

    const onMouseDown = (event: MouseEvent) => {
      if (!shouldStartLasso()) return;
      if (event.button !== 0) return;
      if (isInteractive(event.target as Element | null)) return;

      const rect = renderer.getBoundingClientRect();
      paneRectRef.current = rect;
      setPaneOrigin({ left: rect.left, top: rect.top });
      const start: CanvasPoint = [event.clientX - rect.left, event.clientY - rect.top];
      lassoPendingRef.current = { rect, start };
    };

    const onMouseMove = (event: MouseEvent) => {
      const pending = lassoPendingRef.current;
      if (!isLassoActiveRef.current && pending) {
        const rect = pending.rect;
        const current: CanvasPoint = [event.clientX - rect.left, event.clientY - rect.top];
        const dx = current[0] - pending.start[0];
        const dy = current[1] - pending.start[1];
        if (Math.hypot(dx, dy) >= DRAG_THRESHOLD) {
          setIsLassoActive(true);
          setPaneOrigin({ left: rect.left, top: rect.top });
          setLassoPath([pending.start, current]);
          logger.debug('lasso start at %o', pending.start);
        }
        return;
      }

      if (!isLassoActiveRef.current) return;
      const rect = paneRectRef.current ?? renderer.getBoundingClientRect();
      pendingOriginRef.current = { left: rect.left, top: rect.top };
      paneRectRef.current = rect;
      const point: CanvasPoint = [event.clientX - rect.left, event.clientY - rect.top];
      pendingPointRef.current = point;
      scheduleFlush();
    };

    const onMouseUp = () => {
      if (isLassoActiveRef.current) {
        finishLasso();
        return;
      }

      lassoPendingRef.current = null;
    };

    pane.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('mouseup', onMouseUp, true);
    window.addEventListener('mouseleave', onMouseUp, true);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      pendingPointRef.current = null;
      pendingOriginRef.current = null;
      pane.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('mousemove', onMouseMove, true);
      window.removeEventListener('mouseup', onMouseUp, true);
      window.removeEventListener('mouseleave', onMouseUp, true);
    };
  }, [canvasRef, finishLasso, hasHoveredNode, isEditing, mode, logger]);

  useEffect(() => {
    if (!isLassoActive) return;
    const handlePointerEnd = () => finishLasso();
    window.addEventListener('pointerup', handlePointerEnd, true);
    window.addEventListener('pointercancel', handlePointerEnd, true);
    return () => {
      window.removeEventListener('pointerup', handlePointerEnd, true);
      window.removeEventListener('pointercancel', handlePointerEnd, true);
    };
  }, [finishLasso, isLassoActive]);

  const consumePaneClickSuppression = useCallback(() => {
    if (suppressNextPaneClickRef.current) {
      suppressNextPaneClickRef.current = false;
      return true;
    }
    return false;
  }, []);

  return { isLassoActive, lassoPath, paneOrigin, allowLasso, consumePaneClickSuppression };
}

export default useMindmapLasso;
